"use client";

import { motion } from "framer-motion";
import { TrendingUp, Home, ShoppingBag, Sparkles } from "lucide-react";

/**
 * Cuatro capítulos del control de gastos.
 * Cada capítulo tiene un visual distinto — no son screenshots repetidos.
 */

interface Chapter {
  number: string;
  icon: React.ReactNode;
  title: string;
  pitch: string;
  caption: string;
  category: "ingreso" | "fijo" | "variable" | "hormiga";
}

const CHAPTERS: Chapter[] = [
  {
    number: "01",
    icon: <TrendingUp size={20} />,
    title: "Ingresos",
    pitch: "Lo que entra.",
    caption:
      "Sueldo, freelance, alquileres, lo que sea. Si no tenés claro cuánto te entra cada mes, el resto del control no se sostiene.",
    category: "ingreso",
  },
  {
    number: "02",
    icon: <Home size={20} />,
    title: "Costos fijos",
    pitch: "Lo que no podés evitar.",
    caption:
      "Alquiler, luz, internet, expensas. Aparecen el primer día del mes sí o sí. Tenerlos identificados es el primer paso para planificar el resto.",
    category: "fijo",
  },
  {
    number: "03",
    icon: <ShoppingBag size={20} />,
    title: "Costos variables",
    pitch: "Lo que sí podés ajustar.",
    caption:
      "Súper, delivery, ropa, salidas. Acá viven la mayoría de tus decisiones de consumo. Finbo te muestra dónde tenés margen para ajustar.",
    category: "variable",
  },
  {
    number: "04",
    icon: <Sparkles size={20} />,
    title: "Gastos hormiga",
    pitch: "Lo que se va sin que lo veas.",
    caption:
      "Cafés, kioscos, SUBE, propinas, suscripciones que no usás. Pueden representar hasta el 30% de tus gastos del mes y casi nadie los registra.",
    category: "hormiga",
  },
];

const CATEGORY_TINT: Record<Chapter["category"], string> = {
  ingreso: "rgba(0, 230, 118, 0.10)",
  fijo: "rgba(255, 255, 255, 0.08)",
  variable: "rgba(41, 182, 246, 0.10)",
  hormiga: "rgba(255, 183, 77, 0.10)",
};

const CATEGORY_TEXT: Record<Chapter["category"], string> = {
  ingreso: "text-success",
  fijo: "text-white/80",
  variable: "text-info",
  hormiga: "text-warning",
};

const EASE = [0.16, 1, 0.3, 1] as const;

export default function ChaptersSection() {
  return (
    <section
      id="capitulos"
      data-section="section-chapters"
      className="relative py-24 sm:py-36"
      aria-label="Cuatro capítulos del control de gastos"
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        {/* Section intro */}
        <div data-particle-shield className="mb-20 max-w-3xl">
          <div className="text-[12px] tracking-[0.2em] uppercase text-white/40 mb-5">
            El método
          </div>
          <h2 className="text-[clamp(36px,5.5vw,72px)] font-extrabold leading-[1.05] tracking-tight">
            Cuatro <span className="text-primary">capítulos</span> de tu plata.
          </h2>
          <p className="text-white/55 mt-5 text-lg sm:text-xl max-w-2xl leading-relaxed">
            Finbo no inventa una teoría nueva: usa la que funciona en cualquier
            curso de finanzas personales. Separar ingresos de gastos, gastos
            fijos de variables, y dejar de ignorar lo que se va de a poco.
          </p>
        </div>

        {/* Chapters grid */}
        <div className="space-y-3 sm:space-y-4">
          {CHAPTERS.map((c, i) => (
            <motion.article
              key={c.number}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.05 }}
              data-particle-shield
              className="relative grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 p-7 sm:p-10 rounded-2xl border border-white/[0.05] bg-ink-850/60 backdrop-blur-sm hover:bg-ink-850/80 transition-colors"
            >
              {/* Number + icon column */}
              <div className="md:col-span-3 flex md:flex-col items-start md:items-stretch gap-4 md:gap-6">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: CATEGORY_TINT[c.category] }}
                >
                  <span className={CATEGORY_TEXT[c.category]}>{c.icon}</span>
                </div>
                <div className="flex-1">
                  <div className="text-[11px] tracking-[0.2em] uppercase text-white/35 mb-1">
                    Capítulo {c.number}
                  </div>
                  <div className="text-xl sm:text-2xl font-bold text-white/95">
                    {c.title}
                  </div>
                </div>
              </div>

              {/* Pitch + caption */}
              <div className="md:col-span-9 md:pl-6 md:border-l md:border-white/[0.05]">
                <h3 className="text-[clamp(26px,3.4vw,44px)] font-bold leading-[1.1] mb-4 max-w-2xl text-white/95">
                  {c.pitch}
                </h3>
                <p className="text-white/55 text-base sm:text-lg leading-relaxed max-w-2xl">
                  {c.caption}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
